// Mantine
import { Button, Loader, Anchor } from '@mantine/core'

// Next
import RouterLink from '@/components/ui/RouterLink'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useEffect, useRef, useState } from 'react'

// Dayjs
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/fr'
dayjs.extend(LocalizedFormat)
dayjs.locale('fr')

// Icon
import { IconPhotoOff } from '@tabler/icons-react'

export default function WarrantyDisplay({ code, inModal = false }) {
    const [warranty, setWarranty] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)

    const { requestGet } = useAxiosApi()
    const requestGetRef = useRef(requestGet)
    requestGetRef.current = requestGet

    useEffect(() => {
        let disposed = false
        const loadWarranty = async () => {
            setIsLoading(true)
            setLoadFailed(false)
            setWarranty(null)

            try {
                // Unassociated cards have no warranty record yet.
                const { data, status } = await requestGetRef.current(`/warranties/${encodeURIComponent(code)}`, undefined, {
                    expectedStatuses: [404],
                })
                if (!disposed) setWarranty(status === 404 ? null : data)
            } catch {
                if (!disposed) setLoadFailed(true)
            } finally {
                if (!disposed) setIsLoading(false)
            }
        }
        if (code) loadWarranty()
        return () => { disposed = true }
    }, [code])

    // Resolved server-side: product photo first, then the first Shopify
    // variant image.
    const productImage = warranty?.product_image

    return (
        <div className="@container">
            {isLoading && (
                <div className="flex justify-center my-32">
                    <Loader />
                </div>
            )}

            {!isLoading && loadFailed && (
                <p className="my-10 text-center">Impossible de charger la garantie. Veuillez réessayer.</p>
            )}

            {!isLoading && !loadFailed && !warranty && (
                <div className="flex flex-col gap-3">
                    <span className="text-3xl font-medium text-center">
                        {code}
                    </span>

                    <span className="my-10 text-center">
                        Carte de garantie vide
                    </span>

                    <Button
                        component={RouterLink}
                        href="/warranties/associate"
                        state={{ warrantyCode: code }}
                    >
                        Associer la garantie
                    </Button>
                </div>
            )}

            {!isLoading && warranty && (
                <div className="flex flex-col items-center max-w-[480px] mx-auto">
                    {/* Image */}
                    <div className="w-full flex justify-center py-6">
                        {productImage ? (
                            <img
                                className="w-full max-w-[200px] aspect-square object-contain"
                                src={productImage}
                            />
                        ) : (
                            <div className="w-full max-w-[200px] aspect-square bg-linear-to-b from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center">
                                <IconPhotoOff className="text-gray-200" size={40} />
                            </div>
                        )}
                    </div>

                    {/* Titre */}
                    <div className="text-center mb-6">
                        <Anchor
                            href={'/components/' + warranty?.component?.id}
                            className="text-xl font-semibold tracking-tight text-gray-900 hover:underline"
                        >
                            {warranty?.component?.name}
                        </Anchor>
                        <p className="text-gray-400 text-base tracking-wide mt-1">{code}</p>
                    </div>

                    {/* Infos */}
                    <div className="w-full divide-y divide-gray-100 text-sm mb-6">
                        <div className="flex justify-between py-2.5">
                            <span className="text-gray-500">Début</span>
                            <span>{dayjs(warranty.warranty_start_at).format('D MMMM YYYY')}</span>
                        </div>
                        <div className="flex justify-between py-2.5">
                            <span className="text-gray-500">Fin</span>
                            <span>{dayjs(warranty.warranty_end_at).format('D MMMM YYYY')}</span>
                        </div>
                        <div className="flex justify-between py-2.5">
                            <span className="text-gray-500">Enregistrée</span>
                            <span>{dayjs(warranty.created_at).format('D MMM YYYY à HH:mm')}</span>
                        </div>
                        {warranty.profile && (
                            <div className="flex justify-between py-2.5">
                                <span className="text-gray-500">Par</span>
                                <span>{warranty.profile.name}</span>
                            </div>
                        )}
                        {warranty.retailer && (
                            <div className="flex justify-between py-2.5">
                                <span className="text-gray-500">Revendeur</span>
                                <Anchor component={RouterLink} href={`/retailers/${warranty.retailer.id}`}>
                                    {warranty.retailer.name}
                                </Anchor>
                            </div>
                        )}
                        {warranty.order && (
                            <div className="flex justify-between py-2.5">
                                <span className="text-gray-500">Commande</span>
                                <Anchor href={'/orders/' + warranty.order.id}>
                                    {warranty.order.name}
                                </Anchor>
                            </div>
                        )}
                    </div>

                    {!inModal && (
                        <p className="text-sm text-gray-400 text-center">
                            Pour supprimer, utilisez le menu en haut à droite.
                        </p>
                    )}

                    {inModal && (
                        <Button
                            component={RouterLink}
                            href={'/warranties/' + code}
                            fullWidth
                        >
                            Voir la garantie
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
